import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert
} from '@mui/material';
import { Payment } from '@mui/icons-material';
import { useSchool } from '../contexts/SchoolContext';
import api from '../api';

declare global {
  interface Window {
    PaystackPop?: {
      setup: (config: any) => {
        openIframe: () => void;
      };
    };
  }
}

const formatNaira = (value: number) => `₦${Number(value || 0).toLocaleString('en-NG')}`;

const SchoolPayments: React.FC = () => {
  const { school, payments, makePayment, loading, students } = useSchool();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [pricing, setPricing] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [paystackReady, setPaystackReady] = useState(false);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await api.get('/site-settings');
        setPricing(res.data?.data || {});
      } catch (err) {
        console.error('Failed to load school pricing config', err);
      }
    };

    fetchPricing();
  }, []);

  useEffect(() => {
    const scriptId = 'paystack-inline-script';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => setPaystackReady(true);
      script.onerror = () => {
        console.error('Failed to load Paystack script for school payments');
        setPaystackReady(false);
      };
      document.body.appendChild(script);
    } else if (window.PaystackPop) {
      setPaystackReady(true);
    }
  }, []);

  const perStudentRate = Number(pricing?.schoolAccess?.perStudent || 0);
  const requiredAmount = Math.max(0, (students?.length || 0) * perStudentRate);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAmount('');
    setError(null);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    const paymentAmount = parseFloat(amount);
    if (!amount || Number.isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    if (requiredAmount > 0 && paymentAmount < requiredAmount) {
      setError(`The school payment must be at least ${formatNaira(requiredAmount)} based on ${students?.length || 0} registered students.`);
      return;
    }

    try {
      setError(null);
      setSubmitting(true);

      const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';
      const schoolEmail = school?.email || '';

      if (publicKey && window.PaystackPop && schoolEmail) {
        const reference = `school_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        let paymentHandled = false;

        const handlePaystackSuccess = function (response: any) {
          paymentHandled = true;
          makePayment(paymentAmount, response?.reference || reference, 'paystack')
            .then(() => {
              handleClose();
            })
            .catch((err) => {
              console.error('School payment verification failed:', err);
              setError('Unable to verify the Paystack payment. Please check your payment status and try again.');
              setSubmitting(false);
            });
        };

        const handlePaystackClose = function () {
          if (paymentHandled) return;
          setSubmitting(false);
          setError('Payment was cancelled before completion.');
        };

        const paystackConfig: any = {
          key: publicKey,
          email: schoolEmail,
          amount: Math.round(paymentAmount * 100),
          currency: 'NGN',
          ref: reference,
          callback: handlePaystackSuccess,
          onClose: handlePaystackClose,
        };

        const paystack = window.PaystackPop.setup(paystackConfig);
        paystack.openIframe();
        return;
      }

      await makePayment(paymentAmount);
      handleClose();
    } catch (error) {
      console.error('Error making payment:', error);
      setError('Unable to process the payment at this time.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Payment History
        </Typography>
        <Button variant="contained" startIcon={<Payment />} onClick={handleOpen}>
          Make Payment
        </Button>
      </Box>

      <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(25,118,210,0.04)' }}>
        <Typography variant="h6" gutterBottom>School subscription requirement</Typography>
        <Typography variant="body2" color="text.secondary">
          {students?.length ? `Required payment: ${formatNaira(requiredAmount)} for ${students.length} registered student(s).` : 'The required school payment will be calculated from the number of registered students.'}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Transaction ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment._id}>
                <TableCell>{new Date(payment.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>{formatNaira(Number(payment.amount || 0))}</TableCell>
                <TableCell>
                  <Chip
                    label={payment.status}
                    color={payment.status === 'completed' ? 'success' : payment.status === 'pending' ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{payment.transactionId}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {payments.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          No payments yet
        </Typography>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Make Payment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter the payment amount to activate or extend your school's premium subscription.
            </Typography>
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
              Required minimum: {formatNaira(requiredAmount)} for {students?.length || 0} student(s)
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Amount (₦)"
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!amount || parseFloat(amount) <= 0 || submitting || !paystackReady}
          >
            {submitting ? 'Processing...' : 'Pay Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolPayments;